import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { v1Client } from '@renderer/chat'
import { GlobalEvents } from '@highlight/ipc'
import { useEffect, useMemo } from 'react'
import { useProviders } from './use-providers'
import { useUser } from '@renderer/hooks/useUser'
import { CONNECTIONS_QUERY_KEY, getConnectionsQueryKey } from '@renderer/hooks/integration-query-keys'

export { CONNECTIONS_QUERY_KEY, getConnectionsQueryKey } from '@renderer/hooks/integration-query-keys'

// Track if side effects are currently being handled to avoid duplicates
let isHandlingSideEffects = false

// /connections/providers returns presentation-layer ids (e.g. 'google-calendar',
// 'google-gmail') but the OAuth deep-link callback emits the bare auth-provider
// name ('google'). Collapse presentation ids back to their auth-provider so
// listener names match what's actually emitted.
export const getAuthProviderId = (providerId: string) => providerId.split('-')[0]

export interface Connection {
  id: string
  providerName: string
  providerUserId: string
  email: string
  accessTokenExpiresAt: string
  scopes: Array<string>
  default: boolean
  createdAt: string
  updatedAt: string
  lastAccessAt: string
  // Server-shaped: string values for personal rows, plus server-controlled
  // objects like `orgProvided` on virtual org-shared entries.
  metadata?: Record<string, unknown>
  hasExpired: boolean
  requiresNewDefaultScopes: boolean
}

/**
 * Whether a healthy Ashby OAuth base (`ashby-mcp`) connection exists. The org
 * API-key upgrade (the `ashby` row / `ashby-api-key` catalog entry) can only sit
 * on top of one — the backend 409s otherwise — so both the staged-action
 * prerequisite check and the settings key-dialog gating consult this. Mirrors
 * the backend's `findConnectionForReconnectProvider`; keep them in sync.
 */
export function hasHealthyAshbyMcpBase(connections: Array<Connection> | undefined): boolean {
  return (
    connections?.some(
      (connection) =>
        connection.providerName === 'ashby-mcp' && !connection.hasExpired && !connection.requiresNewDefaultScopes,
    ) ?? false
  )
}

export function useConnections() {
  const { getAuthToken } = useAuth()
  const user = useUser()
  const queryClient = useQueryClient()
  const { data: providers } = useProviders()

  useEffect(() => {
    if (!providers?.length) return

    const handleConnectionUpdate = () => {
      queryClient.invalidateQueries({ queryKey: [CONNECTIONS_QUERY_KEY] })
    }

    const handleCalendarUpdate = () => {
      if (isHandlingSideEffects) return
      isHandlingSideEffects = true
      queryClient.invalidateQueries({ queryKey: ['calendars'] })
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      setTimeout(() => {
        isHandlingSideEffects = false
      }, 2000)
    }

    const calendarAuthProviders = new Set<string>()
    const authProviderIds = new Set<string>()
    for (const provider of providers) {
      const authId = getAuthProviderId(provider.id)
      authProviderIds.add(authId)
      if (provider.tags.includes('calendar')) {
        calendarAuthProviders.add(authId)
      }
    }

    const handlers: Array<{ event: string; handler: () => void }> = []

    for (const id of authProviderIds) {
      const connectEvent = `connections.${id}.connect`
      const disconnectEvent = `connections.${id}.disconnect`

      handlers.push({ event: connectEvent, handler: handleConnectionUpdate })
      handlers.push({ event: disconnectEvent, handler: handleConnectionUpdate })
      GlobalEvents.on(connectEvent, handleConnectionUpdate)
      GlobalEvents.on(disconnectEvent, handleConnectionUpdate)

      if (calendarAuthProviders.has(id)) {
        handlers.push({ event: connectEvent, handler: handleCalendarUpdate })
        handlers.push({ event: disconnectEvent, handler: handleCalendarUpdate })
        GlobalEvents.on(connectEvent, handleCalendarUpdate)
        GlobalEvents.on(disconnectEvent, handleCalendarUpdate)
      }
    }

    return () => {
      for (const { event, handler } of handlers) {
        GlobalEvents.off(event, handler)
      }
    }
  }, [queryClient, providers])

  return useQuery({
    queryKey: getConnectionsQueryKey(user?.id),
    queryFn: async () => {
      const response = await v1Client.GET('/connections', {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      })
      if (response.error) throw new Error('Failed to fetch connections')
      return (response.data?.data ?? []) as Array<Connection>
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Only an invalidation (a connect landing while unmounted) refetches on mount.
    refetchOnMount: (query) => query.state.isInvalidated,
  })
}

export function hasUsableSlackConnection(connections: Array<Connection> | undefined): boolean {
  return connections?.some((connection) => connection.providerName === 'slack' && !connection.hasExpired) ?? false
}

export function useSlackConnected() {
  const { data: connections } = useConnections()
  return hasUsableSlackConnection(connections)
}

const GOOGLE_CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.events'

export function useGoogleCalendarConnected() {
  const { data: connections } = useConnections()
  return useMemo(
    () =>
      connections?.some(
        (c) =>
          (c.providerName === 'google' || (c.providerName === 'google-calendar' && c.metadata?.orgProvided)) &&
          c.scopes?.includes(GOOGLE_CALENDAR_SCOPE),
      ) ?? false,
    [connections],
  )
}
